import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";

/* eslint-disable max-lines-per-function */

/**
 * Polling configuration options
 */
export interface UsePollingOptions {
  /**
   * Polling interval in milliseconds (default: 15000 for 15s)
   */
  interval?: number;

  /**
   * Whether polling is enabled (default: true)
   */
  enabled?: boolean;

  /**
   * Whether to pause polling when the tab is hidden (default: true)
   */
  pauseOnHidden?: boolean;

  /**
   * Maximum number of consecutive errors before stopping (default: 5)
   */
  maxConsecutiveErrors?: number;

  /**
   * Initial backoff delay in ms on error (default: 5000)
   */
  errorBackoffMs?: number;

  /**
   * Maximum backoff delay in ms (default: 60000)
   */
  maxBackoffMs?: number;

  /**
   * Callback on successful poll
   */
  onSuccess?: () => void;

  /**
   * Callback on poll error
   */
  onError?: (error: Error) => void;
}

/**
 * Return type for usePolling hook
 */
export interface UsePollingResult<T> {
  /**
   * Latest data from polling
   */
  data: T | null;

  /**
   * Whether currently loading (initial load or polling)
   */
  loading: boolean;

  /**
   * Error message if last poll failed
   */
  error: string | null;

  /**
   * Number of consecutive errors
   */
  consecutiveErrors: number;

  /**
   * Whether polling is currently active
   */
  isPolling: boolean;

  /**
   * Whether polling is paused (due to hidden tab or errors)
   */
  isPaused: boolean;

  /**
   * Manually trigger a refresh
   */
  refresh: () => Promise<void>;

  /**
   * Pause polling
   */
  pause: () => void;

  /**
   * Resume polling
   */
  resume: () => void;

  /**
   * Reset error count and resume polling
   */
  reset: () => void;
}

// Default intervals per the ADMIN-03 plan
export const POLLING_INTERVALS = {
  /** 15 seconds for active queues (review queue, encoding dashboard) */
  ACTIVE_QUEUE: 15_000,
  /** 30 seconds for detail views (submission detail, title detail) */
  DETAIL_VIEW: 30_000,
  /** 60 seconds for less frequently changing data */
  SLOW: 60_000,
} as const;

/**
 * Generic polling hook for admin status updates
 *
 * Features per ADMIN-03 plan:
 * - Configurable polling intervals (15s for queues, 30s for details)
 * - Exponential backoff on errors
 * - Pause when tab is hidden (uses Page Visibility API)
 * - Manual refresh capability
 * - Error count tracking with auto-stop
 *
 * @example
 * ```tsx
 * const { data, loading, error, refresh } = usePolling(
 *   async () => {
 *     const response = await fetch('/api/submissions');
 *     return response.json();
 *   },
 *   { interval: POLLING_INTERVALS.ACTIVE_QUEUE }
 * );
 * ```
 */
export function usePolling<T>(fetchFn: () => Promise<T>, options: UsePollingOptions = {}): UsePollingResult<T> {
  const {
    interval = POLLING_INTERVALS.ACTIVE_QUEUE,
    enabled = true,
    pauseOnHidden = true,
    maxConsecutiveErrors = 5,
    errorBackoffMs = 5_000,
    maxBackoffMs = 60_000,
    onSuccess,
    onError,
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);
  const [isPolling, setIsPolling] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [manualPause, setManualPause] = useState(false);

  // Refs for cleanup and state tracking
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const fetchFnRef = useRef(fetchFn);
  const currentBackoffRef = useRef(errorBackoffMs);

  // Update fetchFn ref when it changes
  useEffect(() => {
    fetchFnRef.current = fetchFn;
  }, [fetchFn]);

  /**
   * Calculate backoff delay with exponential growth
   */
  const calculateBackoff = useCallback(
    (errorCount: number): number => {
      // Exponential backoff: base * 2^(errors-1), capped at max
      const backoff = errorBackoffMs * Math.pow(2, errorCount - 1);
      return Math.min(backoff, maxBackoffMs);
    },
    [errorBackoffMs, maxBackoffMs],
  );

  /**
   * Execute a single poll
   */
  const executePoll = useCallback(async (): Promise<void> => {
    if (!mountedRef.current) return;

    try {
      const result = await fetchFnRef.current();

      if (!mountedRef.current) return;

      setData(result);
      setError(null);
      setConsecutiveErrors(0);
      currentBackoffRef.current = errorBackoffMs;
      onSuccess?.();
    } catch (err) {
      if (!mountedRef.current) return;

      const errorMessage = err instanceof Error ? err.message : "Polling failed";
      setError(errorMessage);
      setConsecutiveErrors((prev) => prev + 1);
      currentBackoffRef.current = calculateBackoff(consecutiveErrors + 1);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [calculateBackoff, consecutiveErrors, errorBackoffMs, onError, onSuccess]);

  /**
   * Schedule the next poll
   */
  const scheduleNextPoll = useCallback(() => {
    if (!mountedRef.current || !enabled || manualPause || isPaused) return;
    if (consecutiveErrors >= maxConsecutiveErrors) return;

    // Use backoff delay if there are errors, otherwise normal interval
    const delay = consecutiveErrors > 0 ? currentBackoffRef.current : interval;

    timeoutRef.current = setTimeout(() => {
      if (mountedRef.current) {
        setIsPolling(true);
        void executePoll().finally(() => {
          if (mountedRef.current) {
            setIsPolling(false);
            scheduleNextPoll();
          }
        });
      }
    }, delay);
  }, [enabled, manualPause, isPaused, consecutiveErrors, maxConsecutiveErrors, interval, executePoll]);

  /**
   * Manual refresh
   */
  const refresh = useCallback(async (): Promise<void> => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setLoading(true);
    await executePoll();

    // Reschedule polling after manual refresh
    scheduleNextPoll();
  }, [executePoll, scheduleNextPoll]);

  /**
   * Pause polling
   */
  const pause = useCallback(() => {
    setManualPause(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  /**
   * Resume polling
   */
  const resume = useCallback(() => {
    setManualPause(false);
    scheduleNextPoll();
  }, [scheduleNextPoll]);

  /**
   * Reset error count and resume
   */
  const reset = useCallback(() => {
    setConsecutiveErrors(0);
    setError(null);
    currentBackoffRef.current = errorBackoffMs;
    setManualPause(false);
    scheduleNextPoll();
  }, [errorBackoffMs, scheduleNextPoll]);

  /**
   * Handle visibility change (pause on hidden tab)
   */
  useEffect(() => {
    if (!pauseOnHidden) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsPaused(true);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      } else {
        setIsPaused(false);
        // Resume polling when tab becomes visible
        if (enabled && !manualPause) {
          scheduleNextPoll();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [pauseOnHidden, enabled, manualPause, scheduleNextPoll]);

  /**
   * Initial fetch and polling setup
   */
  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      // Initial fetch
      void executePoll().finally(() => {
        if (mountedRef.current) {
          scheduleNextPoll();
        }
      });
    }

    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [enabled]); // Only re-run on enabled change

  return {
    data,
    loading,
    error,
    consecutiveErrors,
    isPolling,
    isPaused: isPaused || manualPause,
    refresh,
    pause,
    resume,
    reset,
  };
}

/**
 * Specialized hook for polling submission status updates
 *
 * @example
 * ```tsx
 * const { data, loading, refresh } = useSubmissionStatusPolling(
 *   submissionId,
 *   { enabled: !!submissionId }
 * );
 * ```
 */
export function useSubmissionStatusPolling(
  submissionId: string | null,
  options: Omit<UsePollingOptions, "interval"> & { interval?: number } = {},
) {
  const { accessToken } = useAuth();
  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  const fetchSubmission = useCallback(async (): Promise<unknown> => {
    if (!submissionId || !accessToken) {
      throw new Error("Missing submission ID or auth token");
    }

    const response = await fetch(`${API_BASE}/v1/admin/submissions/${submissionId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch submission: ${response.statusText}`);
    }

    const json: unknown = await response.json();
    return json;
  }, [submissionId, accessToken, API_BASE]);

  return usePolling(fetchSubmission, {
    interval: POLLING_INTERVALS.DETAIL_VIEW,
    enabled: !!submissionId && !!accessToken,
    ...options,
  });
}
